import { AssortmentEntry, MacroplanogramEntry, TemperatureRating } from 'src/graphql/generated/graphql';
import { Action, CapacityTuple } from './AgileUploadRow';
import { parseMacroData } from '../utils/FixtureDataUtils';

// Flattened Model that will parse resolver models into a common one
export interface AssortmentModel {
  // AMS fields
  macroplanogramId?: string;
  macroplanogramName?: string;
  zoneName?: string; // duplicated with macroplanogramName
  subcategoryId?: string;
  subcategoryName?: string;
  entryId?: string;
  startTime?: number;
  endTime?: number;

  // Common fields
  asin: string;
  title: string;
  image?: string;
  uphl1: string;
  uphl2: string;
  uphl3: string;
  zoneId?: string;
  shrinkCost?: number;
  shrinkPerOps?: number;
  shrinkPerOps13week?: number;
  shrinkPerOps26week?: number;
  shrinkPerOps52week?: number;
  movingAvgSales13week?: number;
  movingAvgSales26week?: number;
  movingAvgSales52week?: number;

  // From AgileRecommendationTypes
  availableFacings: number;
  facingToRemove: number;
  fohCapacity: CapacityTuple;
  remainingFacings: number;
  remainingCapacity: number;
  difference: number;

  // From AgileUploadRow
  facingUnits: number;
  procurable?: boolean;
  buyable?: boolean;
  temperatureRating?: TemperatureRating;
  action?: Action;
  existingFohCapacity?: number;
}

export function parseAssortmentSwapRecommendation(record: AssortmentEntry): AssortmentModel {
  return {
    // AMS fields
    macroplanogramId: record.macroplanogramId,
    subcategoryId: record.subcategoryId,
    entryId: record.entryId,
    startTime: record.startTime,
    endTime: record.endTime,

    // Common fields
    asin: record.asin,
    title: record.asinMetadata?.title ?? 'Unknown',
    image: record.asinMetadata?.image,
    uphl1: record.asinMetadata?.uph?.uphl1,
    uphl2: record.asinMetadata?.uph?.uphl2,
    uphl3: record.asinMetadata?.uph?.uphl3,
    shrinkCost: record.asinMetadata?.performanceData?.shrinkCost,
    shrinkPerOps: record.asinMetadata?.performanceData?.shrinkPerOps,
    shrinkPerOps13week: record.asinMetadata?.performanceData?.shrinkPerOps13week,
    shrinkPerOps26week: record.asinMetadata?.performanceData?.shrinkPerOps26week,
    shrinkPerOps52week: record.asinMetadata?.performanceData?.shrinkPerOps52week,
    movingAvgSales13week: record.asinMetadata?.performanceData?.movingAvgSales13week,
    movingAvgSales26week: record.asinMetadata?.performanceData?.movingAvgSales26week,
    movingAvgSales52week: record.asinMetadata?.performanceData?.movingAvgSales52week,

    // From AgileRecommendationTypes
    availableFacings: record.facingUnits,
    fohCapacity: {
      capacity: (record.fohCapacity as number) || null,
      errorMsg: record.asinMetadata?.capacityUnitPerFacing?.errorMsg || null,
      jumbleFactor: record.asinMetadata?.capacityUnitPerFacing?.jumbleFactor || null,
    },
    existingFohCapacity: Number(record.fohCapacity),
    // From AgileUploadRow
    facingUnits: Number(record.facingUnits),
    procurable: record.asinMetadata?.procurable,
    buyable: record.asinMetadata?.buyable,
    temperatureRating: record.asinMetadata?.temperatureRating as TemperatureRating,
  } as AssortmentModel;
}

export function decorateAssortmentModelWithZoneMetadata(
  assortment: AssortmentModel,
  macroplanogramEntries: MacroplanogramEntry[],
): AssortmentModel {
  // Find matching macroEntry
  const matchedMacroEntry = macroplanogramEntries.find((macro) => macro.entryId === assortment.macroplanogramId);

  if (!matchedMacroEntry) {
    console.error(
      `Not matched macroplanogram Id for assortment ${assortment.entryId}:${assortment.asin} => ${assortment.macroplanogramId} in  ${JSON.stringify(macroplanogramEntries)}`,
    );
    return {
      ...assortment,
      macroplanogramName: 'Unknown',
      zoneName: 'Unknown',
      subcategoryName: 'Unknown',
    };
  } else {
    const matchedMacroData = parseMacroData([matchedMacroEntry])[0];

    return {
      ...assortment,
      macroplanogramName: matchedMacroData.zoneName,
      zoneName: matchedMacroData.zoneName,
      subcategoryName: matchedMacroData.concatnatedSubcategoryName,
    };
  }
}
